<template>
  <div class="newprogressbar" ref="progressBar" @mousedown="startDrag" @mousemove="onDrag" @mouseup="stopDrag"
    @mouseleave="stopDrag" @touchstart="startDrag" @touchmove="onDrag" @touchend="stopDrag" @touchcancel="stopDrag">
    <div class="redthing"></div>

    <div v-if="waveformpng" ref="waveformEl" class="waveform" :style="waveformStyle"></div>
    <div class="grid" :style="gridStyle">
      <div v-for="markerIndex in gridItems" :key="markerIndex" class="gridthing"
        :style="getBarPos(markerIndex)"></div>
    </div>
  </div>

  <div class="time-and-lufs-container mt-1">
    <div class="time">{{ formatTime(displayTime) }} / {{ formatTime(duration) }}</div>
    <div class="lufs-display" v-if="liveMetering">
      <template v-for="(metric, index) in lufsMetrics" :key="metric.key">
        <span v-if="index > 0" class="lufs_separator" aria-hidden="true">/</span>
        <span class="lufs_metric" tabindex="0" :aria-describedby="`lufs-${metric.key}-tooltip`">
          {{ metric.value.toFixed(1) }}
          <span :id="`lufs-${metric.key}-tooltip`" class="lufs_popover" role="tooltip">
            {{ $t(metric.labelKey) }}
          </span>
        </span>
      </template>
    </div>
  </div>
</template>

<script>

import { hapticMixin } from '@/scripts/haptic';
import { settings } from '@/scripts/services/SettingsService';
export default {
  emits: ['drag-start', 'seek'],
  mixins: [hapticMixin],
  props: {
    currentTime: Number,
    duration: Number,
    waveformpng: String,
    isPlaying: Boolean,
    bpm: [Number, String],
    lufsMomentary: {
      type: Number,
      default: null
    },
    lufsShortTerm: {
      type: Number,
      default: null
    },
    lufsIntegrated: {
      type: Number,
      default: null
    }
  },

  data() {
    return {
      isDragging: false,
      dragPercent: 0,
      dragStartPosition: null,
      dragStartPercent: null
    };
  },

  computed: {
    liveMetering(){
      return settings.state.liveLoudnessMetering
    },
    waveformStyle() {
      let percent;

      if (this.isDragging) {
        percent = this.dragPercent;
      } else {
        // normal playback
        percent = (this.currentTime / this.duration) * 100;
      }

      return {
        backgroundImage: `url(${this.waveformpng})`,
        transform: `translateX(-${percent}%)`
      };
    },
    gridStyle() {
      let percent;

      if (this.isDragging) {
        percent = this.dragPercent;
      } else {
        // normal playback
        percent = (this.currentTime / this.duration) * 100;
      }

      return {
        transform: `translateX(-${percent}%)`
      };
    },
    displayTime() {
      return this.isDragging
        ? (this.dragPercent / 100) * this.duration
        : this.currentTime;
    },
    lufsMetrics() {
      return [
        { key: 'momentary', labelKey: 'lufs.momentary', value: this.lufsMomentary },
        { key: 'short-term', labelKey: 'lufs.shortTerm', value: this.lufsShortTerm },
        { key: 'integrated', labelKey: 'lufs.integrated', value: this.lufsIntegrated }
      ].filter(({ value }) => value !== null);
    },
    gridItems() {
      if (!this.bpm || !this.duration) return [];

      const beatsPerSecond = this.bpm / 60;
      const totalBeats = Math.ceil(this.duration * beatsPerSecond);
      const subdivision = 2
      const markerCount = totalBeats * subdivision;

      return Array.from(
        { length: Math.floor(markerCount / 4) },
        (_, index) => ((index + 1) * 4) - 1
      );
    }
  },

  methods: {
    formatTime(sec) {
      if (!sec || isNaN(sec)) return "00:00";
      const m = Math.floor(sec / 60).toString().padStart(2, "0");
      const s = Math.floor(sec % 60).toString().padStart(2, "0");
      const ms = (Math.floor(sec * 100) % 100).toString().padStart(2, "0");
      return `${m}:${s}:${ms}`;
    },

    getPercentFromMouse(e) {
      const rect = this.$refs.progressBar.getBoundingClientRect();
      const clientX = this.getClientX(e);
      let px = this.dragStartPosition - clientX;
      let percent = this.dragStartPercent + (px / rect.width) * 100;
      return Math.max(0, Math.min(percent, 100));
    },

    getClientX(e) {
      if (e.touches && e.touches.length > 0) {
        return e.touches[0].clientX;
      }
      return e.clientX;
    },
    getBarPos(index) {
      const beatsPerSecond = this.bpm / 60;
      const subdivision = 4;

      // каждая линия = 1/16 ноты
      const secondsPerLine = 1 / (beatsPerSecond * subdivision);

      // время конкретной линии
      const timeOfLine = secondsPerLine * index;

      // конвертация времени в процент внутренней ширины (200%)
      const percent = (timeOfLine / this.duration) * 200;

      return {
        left: `${percent}%`
      };
    },
    startDrag(e) {
      this.triggerHaptic();
      this.isDragging = true;
      this.dragStartPosition = this.getClientX(e);
      if (this.duration <= 0 || isNaN(this.duration)) {
        this.dragPercent = 0;
      } else {
        this.dragPercent = (this.currentTime / this.duration) * 100;
      }
      this.dragStartPercent = this.dragPercent
      this.$emit("drag-start")
    },

    onDrag(e) {
      if (!this.isDragging) return;
      this.dragPercent = this.getPercentFromMouse(e);
    },

    stopDrag() {
      //console.log("Stop drag!")
      if (!this.isDragging) return;
      this.isDragging = false;

      let t;
      if (this.duration <= 0 || isNaN(this.duration)) {
        t = 0;
      } else {
        t = (this.dragPercent / 100) * this.duration;
      }
      this.$emit("seek", t);
      this.triggerHaptic();
    }
  }
};
</script>

<style scoped>
.newprogressbar {
  position: relative;
  width: 100%;
  height: 4rem;
  background-color: transparent;
  overflow: hidden;
  border: 0;
  border-bottom: 1px solid #989898;
  cursor: grab;
}

.newprogressbar:active {
  cursor: grabbing;
}


.redthing {
  position: absolute;
  width: 1px;
  height: 100%;
  background-color: rgb(192, 0, 0);
  left: 50%;
  z-index: 999;
}

.waveform {
  position: absolute;
  top: 0;
  left: 50%;
  width: 200%;
  height: 100%;
  background-size: 100% 100%;
  filter: invert(49.4118%);
  will-change: transform;

}

.time {
  font-family: 'Doto', sans-serif;
  font-weight: 900;
  white-space: nowrap;
}

.time-and-lufs-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.25rem; /* Equivalent to mt-1 */
  font-family: 'Doto', sans-serif;
  font-weight: 900;
  font-size: 14px; /* Adjust as needed */
}

.lufs-display {
  display: flex;
  align-items: center;
  gap: 8px; /* Space between LUFS values */
}

.lufs_metric {
  position: relative;
  white-space: nowrap;
  cursor: help;
}

.lufs_metric:focus-visible {
  outline: 1px solid #989898;
  outline-offset: 2px;
}

.lufs_popover {
  position: absolute;
  z-index: 10;
  top: calc(100% + 8px);
  right: 0;
  padding: 6px 10px;
  border: 1px solid rgb(47 47 47);
  border-radius: 0.5rem;
  background: rgb(47 47 47 / 80%);
  box-shadow: inset 0 2px 4px 0 #ffffff06;
  backdrop-filter: blur(12.3px);
  color: var(--primary-text-color);
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  opacity: 0;
  pointer-events: none;
  transform: translateY(-2px);
  transition: opacity 120ms ease, transform 120ms ease;
  visibility: hidden;
  white-space: nowrap;
  -webkit-backdrop-filter: blur(12.3px);
}

.lufs_metric:hover .lufs_popover,
.lufs_metric:focus-visible .lufs_popover {
  opacity: 1;
  transform: translateY(0);
  visibility: visible;
}

.grid {
  position: absolute;
  top: 0;
  left: 50%;
  width: 200%;
  height: 100%;
}

.gridthing {
  position: absolute;
  top: 0;
  width: 1px;
  height: 100%;
  background-color: #989898;
}
</style>
